import { FormControl } from '@mui/material';
import dayjs from 'dayjs';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { useEffect, useRef, useState } from 'react';
import { PARAGRAPH, SUBTITLE, TITLE, Title } from '../components';
import { CampaignSummary } from '../components/CampaignSummary';
import {
  CampaignBudgets,
  CampaignNotes,
  DayStrat,
  FibLibSummary,
  PeakPercentage,
  StationSplit,
  AdMirroring
} from '../components/Summary';
import { useDownloadPdf } from '../context/PdfContext';
import { useAppSelector } from '../redux/hooks';
import { RootState } from '../redux/store';
import styled from 'styled-components';
import DetailSummary from '../components/Summary/DetailSummary';
import { mapDemographics } from '../util';
import { formatDate } from 'date-fns';
import { DATE_FORMAT } from '../constants';

const StyledContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  width: 100%;
  margin-bottom: 16px;
  margin-top: 16px;
`;

const StyledBudgetsContainer = styled.div`
  max-width: 80%;
`;

const StyledParent = styled.div`
  max-width: 100%;
`;

const StyledText = styled.p`
  color: white;
  max-width: 30%;
  word-wrap: break-word;
`;

const FourthPage = () => {
  const [show, setShow] = useState<boolean>(false);
  const documentRef = useRef<any>();
  const summaryParentRef = useRef<any>();
  const summaryRef = useRef<any>();

  const { downloadPdf, setDownloadPdf } = useDownloadPdf();

  const {
    agencyName,
    advertiserName,
    campaignName,
    advertiserDiscount,
    demographic,
    commencingDay,
    campaignLength,
    campaignNotes
  } = useAppSelector((state: RootState) => state.detail);

  useEffect(() => {
    if (downloadPdf) {
      setShow(true);

      setTimeout(() => {
        summaryRef.current.showExtraSection();
      }, 1000);

      // if we don't put a timeout then the section shrinks before the canvas image is generated
      setTimeout(() => {
        generatePDF();
        setDownloadPdf(false);
        summaryRef.current.hideExtraSection();
      }, 2000);
    }
  }, [downloadPdf]);

  const printHeading = (pdf: jsPDF) => {
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(16);
    pdf.text(`${agencyName} - ${advertiserName} - ${campaignName}`, 10, 12);
    pdf.setFontSize(10);
    pdf.text(
      `generated ${dayjs(Date.now()).format('h:mma DD/MM/YYYY')}`,
      10,
      16
    );
  };

  const generatePDF = async () => {
    const element = documentRef.current as HTMLCanvasElement;
    const canvas = await html2canvas(element, {
      backgroundColor: '#1b1b1b',
      scale: 1
    });

    const imgData = canvas.toDataURL('image/png');
    const doc = new jsPDF('p', 'mm', 'a4');

    var pageWidth = 210;
    var pageHeight = 295;

    var imgHeight = (canvas.height * pageWidth) / canvas.width;
    var heightLeft = imgHeight;

    var position = 40;

    printHeading(doc);
    doc.addImage(
      imgData,
      'PNG',
      10,
      position - 10 * 2,
      pageWidth - 10 * 2,
      imgHeight
    );
    heightLeft -= pageHeight;

    while (heightLeft >= 0) {
      position = heightLeft - imgHeight + 30;
      doc.addPage();
      doc.addImage(
        imgData,
        'PNG',
        10,
        position,
        pageWidth - 10 * 2,
        imgHeight - 10 * 2
      );
      heightLeft -= pageHeight;
    }

    doc.save(
      `tvnz-audience-trading-calculator-output-${agencyName}-${advertiserName}-${campaignName}-${dayjs(Date.now()).format('hmma-DD-MM-YYYY')}.pdf`
    );
    setShow(false);
  };

  const getDates = (): string => {
    if (!campaignLength.startDate || !campaignLength.endDate) return '';

    return `${formatDate(campaignLength.startDate, DATE_FORMAT)} - ${formatDate(campaignLength.endDate, DATE_FORMAT)}`;
  };

  const details = [
    { title: 'Agency', content: agencyName },
    {
      title: 'Discount',
      content: (advertiserDiscount * 100).toString() + '%'
    },
    { title: 'Advertiser', content: advertiserName },
    { title: 'Campaign Name', content: campaignName },
    { title: 'W/C Day', content: commencingDay },
    {
      title: 'Dates',
      content: getDates()
    }
  ];

  const getDetails = () => {
    if (!demographic) return [];

    const demographics = mapDemographics(demographic);

    demographics.map((item) => {
      details.push(item);
    });

    return details;
  };

  let Summary = (
    <div ref={summaryParentRef}>
      <CampaignSummary ref={summaryRef} />
    </div>
  );

  return (
    <StyledParent ref={documentRef}>
      <FormControl>
        {!show ? (
          <>
            <Title
              Title={TITLE.CAMPAIGN_PLANNER}
              Subtitle={SUBTITLE.STEP_FOUR}
              Paragraph={PARAGRAPH.PARAGRAPH_FOUR}
            />
            <CampaignNotes />
            {Summary}
            <CampaignBudgets />
            <StyledContainer>
              <DetailSummary
                heading="Campaign Details"
                details={getDetails()}
              />
            </StyledContainer>
            <StyledContainer>
              <StationSplit />
              <PeakPercentage />
            </StyledContainer>

            <DayStrat />
            <StyledContainer>
              <FibLibSummary />
              <AdMirroring />
            </StyledContainer>
          </>
        ) : (
          <>
            <StyledBudgetsContainer>
              <DetailSummary
                heading="Campaign Details"
                details={getDetails()}
              />
              {Summary}
              <CampaignBudgets />
              <StyledContainer>
                <StationSplit />
                <PeakPercentage />
              </StyledContainer>
              <DayStrat />
              <StyledContainer>
                <FibLibSummary />
                <AdMirroring />
              </StyledContainer>
              <h2>Campaign Notes</h2>
              <StyledText>{campaignNotes}</StyledText>
            </StyledBudgetsContainer>
          </>
        )}
      </FormControl>
    </StyledParent>
  );
};

export default FourthPage;
